import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { getTz } from "@/lib/tz";  // rc130 — zona horaria desde Ajustes

/** Helper para combinar clases Tailwind con merge inteligente. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Formatea un monto en MXN. */
export function formatMXN(value: number | string): string {
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return "$0.00";
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
  }).format(n);
}

/**
 * Timezone canónica del producto. ByteISP opera en México (CDMX) — todos los
 * timestamps se renderean en hora local mexicana, sin importar la TZ del
 * dispositivo del operador (que puede estar mal configurada o el técnico
 * estar viajando). Cron de cortes corre 8:00 AM CDMX, así que mantener esto
 * fijo es crítico para que la auditoría se lea en la misma hora que el cron.
 */

/** Formatea fecha a DD/MM/YYYY (en hora CDMX). */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("es-MX", {
    timeZone: getTz(),
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
}

/** Formatea fecha-hora a DD/MM/YYYY HH:MM:SS 24h (en hora CDMX). */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  const fecha = new Intl.DateTimeFormat("es-MX", {
    timeZone: getTz(),
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
  const hora = new Intl.DateTimeFormat("es-MX", {
    timeZone: getTz(),
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(d);
  return `${fecha} ${hora}`;
}

/** Retorna YYYY-MM-DD en zona local para date inputs. */
export function toISODate(value: Date): string {
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, "0");
  const d = String(value.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
